import { useNavigate, useParams } from "react-router-dom";
import WorkOrder from "../../models/workOrder";
import workOrderStatusHelper from "../../models/workOrderStatusHelper";
import { useContext, useEffect, useState } from "react";
import workOrderService from "../../services/workOrderService";
import Moment from "react-moment";
import dateTimeHelper from "../../util/datetimeHelper";
import WorkOrderLink from "../../components/work/work-order/WorkOrderLink";
import AccountLink from "../../components/work/AccountLink";
import SubscriptionContext from "../../contexts/subscriptionContext";
import SubscriptionContextModel from "../../contexts/subscriptionContextModel";
import workOrderTypeHelper from "../../models/workOrderTypeHelper";
import workOrderPriorityHelper from "../../models/workOrderPriorityHelper";
import WorkOrderStatus from "../../models/workOrderStatus";
import DeclineWorkOrderWindow from "../../components/work/work-order/DeclineWorkOrderWindow";
import AcceptWorkOrderWindow from "../../components/work/work-order/AcceptWorkOrderWindow";
import ScheduleWorkOrderWindow from "../../components/work/work-order/ScheduleWorkOrderWindow";
import WorkOrderDispatchesView from "./WorkOrderDispatchesView";
import WorkOrderFileUpload from "../../components/work/work-order/WorkOrderFileUpload";
import WorkOrderFileList from "../../components/work/work-order/WorkOrderFileList";
import ServiceProvider from "../../models/serviceProvider";
import Page from "../../components/work/layout/Page";

const WorkOrderView: React.FC = () => {
  const navigate = useNavigate();

  const { id: idParam } = useParams();

  const [workOrder, setWorkOrder] = useState<WorkOrder | undefined>();
  // const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [, setErrorMessage] = useState<string | undefined>();

  const { subscription } =
    useContext<SubscriptionContextModel>(SubscriptionContext);

  const [showAcceptWorkOrderWindow, setShowAcceptWorkOrderWindow] =
    useState<boolean>(false);

  const [showDeclineWorkOrderWindow, setShowDeclineWorkOrderWindow] =
    useState<boolean>(false);

  const [showScheduleWorkOrderWindow, setShowScheduleWorkOrderWindow] =
    useState<boolean>(false);

  const [tabsEl, setTabsEl] = useState<HTMLUListElement | null>(null);

  const [showDispatchesTab, setShowDispatchesTab] = useState<boolean>(false);
  const [showFilesTab, setShowFilesTab] = useState<boolean>(false);

  const [refreshImageListIndex, setRefreshImageListIndex] = useState<number>(0);

  // useEffect(() => {
  //   console.log("WorkOrderView-refreshImageListIndex", refreshImageListIndex);
  // }, [refreshImageListIndex]);

  useEffect(() => {
    if (!tabsEl) {
      return;
    }

    // console.log("tabs-events-add-init");

    const tabButtons = tabsEl.querySelectorAll<HTMLButtonElement>(
      '[data-bs-toggle="tab"]',
    );

    const handleShown = (event: Event) => {
      //const e = event as CustomEvent;
      // const e = event as any;
      // console.log("Opened tab:", e.target);
      // console.log("Previous tab:", e.relatedTarget);

      destoryAllTabs();

      const target = event.target as HTMLButtonElement;
      if (target.dataset.bsTarget === "#dispatches") {
        //console.log("Load dispatches data");
        setShowDispatchesTab(true);
      } else if (target.dataset.bsTarget === "#files") {
        //console.log("Load files data");
        setShowFilesTab(true);
      }
    };

    const destoryAllTabs = () => {
      setShowDispatchesTab(false);
      setShowFilesTab(false);
    };

    const handleHidden = (event: Event) => {
      // const e = event as CustomEvent;
      // console.log("Closed tab:", e.target);
    };

    tabButtons.forEach((tab) => {
      tab.addEventListener("shown.bs.tab", handleShown);
      tab.addEventListener("hidden.bs.tab", handleHidden);
    });

    return () => {
      tabButtons.forEach((tab) => {
        tab.removeEventListener("shown.bs.tab", handleShown);
        tab.removeEventListener("hidden.bs.tab", handleHidden);
      });
    };
  }, [tabsEl]);

  const hasDispatchesView = (workOrder: WorkOrder) => {
    return workOrder.canSeeDispatches && workOrder.canDispatch;
  };

  useEffect(() => {
    const init = async () => {
      const workOrderNumber = idParam;

      if (!workOrderNumber) {
        return;
      }

      const { data: workOrder } = await workOrderService.getWorkOrderByNumber(
        subscription.idSubscription,
        workOrderNumber,
      );
      setWorkOrder(workOrder);

      if (workOrder) {
        if (hasDispatchesView(workOrder)) {
          setShowDispatchesTab(true);
        } else {
          setShowFilesTab(true);
        }
      }
    };

    init();
  }, [idParam, subscription]);

  const loadWorkOrder = async (
    idSubscription: number,
    workOrderNumber: string,
  ) => {
    try {
      const { data: workOrder } = await workOrderService.getWorkOrderByNumber(
        idSubscription,
        workOrderNumber,
      );
      setWorkOrder(workOrder);
    } catch (error: any) {
      setErrorMessage(error.response.data);
    }
  };

  const onAcceptWorkOrder = async () => {
    if (!workOrder) {
      return;
    }

    await workOrderService.acceptWorkOrder(
      subscription.idSubscription,
      workOrder.workOrderNumber,
    );

    await loadWorkOrder(subscription.idSubscription, workOrder.workOrderNumber);
  };

  const onDeclineWorkOrder = async (
    declineWorkOrderReason: string | undefined,
  ) => {
    if (!workOrder) {
      return;
    }

    await workOrderService.declineWorkOrder(
      subscription.idSubscription,
      workOrder.workOrderNumber,
      declineWorkOrderReason,
    );

    await loadWorkOrder(subscription.idSubscription, workOrder.workOrderNumber);
  };

  const onScheduleWorkOrder = async (scheduledDate: Date | undefined) => {
    if (!workOrder || !scheduledDate) {
      return;
    }

    await workOrderService.scheduleWorkOrder(
      subscription.idSubscription,
      workOrder.workOrderNumber,
      scheduledDate,
    );

    await loadWorkOrder(subscription.idSubscription, workOrder.workOrderNumber);
  };

  const onDispatchWorkOrderClick = () => {
    if (!workOrder) {
      return;
    }

    navigate(`/work/work-orders/dispatch/${workOrder.workOrderNumber}`);
  };

  // const onEditClick = (workOrder: WorkOrder) => {
  //   navigate(`/work/work-orders/edit/${workOrder.idWorkOrder}`);
  // };

  if (!workOrder) {
    return <div></div>;
  }

  return (
    <Page
      pageTitle="Work Order"
      breadcrumbs={[
        { linkName: "Work Orders", linkUlr: "/work/work-orders" },
        { linkName: workOrder.workOrderNumber, linkUlr: "" },
      ]}
    >
      <section className="section">
        {/* Work Order */}
        <div className="card p-4">
          <div className="mb-3">
            <div className="d-flex align-items-center">
              <div style={{ fontSize: "30px" }}>
                Work Order #
                <span className="ms-4">{workOrder.workOrderNumber}</span>
              </div>

              <div>
                {workOrder.workOrderStatus ===
                  WorkOrderStatus.PendingAcceptance && (
                  <>
                    <button
                      className="btn btn-primary"
                      style={{ marginLeft: "20px" }}
                      onClick={() => setShowAcceptWorkOrderWindow(true)}
                    >
                      Accept
                    </button>
                    <button
                      className="btn btn-primary"
                      style={{ marginLeft: "20px" }}
                      onClick={() => setShowDeclineWorkOrderWindow(true)}
                    >
                      Decline
                    </button>
                  </>
                )}

                {workOrder.workOrderStatus ===
                  WorkOrderStatus.AcceptedPendingSchedule && (
                  <>
                    <button
                      className="btn btn-primary"
                      style={{ marginLeft: "20px" }}
                      onClick={() => setShowScheduleWorkOrderWindow(true)}
                    >
                      Schedule
                    </button>
                  </>
                )}

                {workOrder.canDispatch && (
                  <button
                    className="btn btn-primary"
                    style={{ marginLeft: "20px" }}
                    onClick={() => onDispatchWorkOrderClick()}
                  >
                    Dispatch
                  </button>
                )}
              </div>
            </div>

            {workOrder.parentWorkOrderNumber && (
              <div>
                Reference Work Numer #
                <span className="ms-3">
                  <WorkOrderLink
                    workOrderNumber={workOrder.parentWorkOrderNumber}
                    canNavigate={workOrder.parentWorkOrder !== null}
                  />
                </span>
              </div>
            )}
            {!workOrder.parentWorkOrderNumber && (
              <div>
                Account
                <span className="ms-3">
                  <b>
                    <AccountLink account={workOrder.account} />
                  </b>
                </span>
              </div>
            )}

            <div className="mt-3">
              <span className="me-2">Status</span>
              <span
                className={
                  workOrderStatusHelper.getBadgeClass(
                    workOrder.workOrderStatus,
                  ) + " me-2"
                }
              >
                {workOrderStatusHelper.getDisplayValue(
                  workOrder.workOrderStatus,
                )}
              </span>
              {workOrder.declineReason && (
                <span className="me-2">
                  Decline Reason{" "}
                  <span
                    className={workOrderStatusHelper.getBadgeClass(
                      workOrder.workOrderStatus,
                    )}
                  >
                    {workOrder.declineReason}
                  </span>
                </span>
              )}
            </div>
          </div>

          <div className="card-body">
            <div className="row">
              <div className="col-md-6">
                <h5>Work Order</h5>

                <div className="row">
                  <div className="col-lg-3 col-md-4 text-muted">Trade</div>
                  <div className="col-lg-9 col-md-8">
                    {workOrder.trade.tradeName}
                  </div>
                </div>

                <div className="row">
                  <div className="col-lg-3 col-md-4 text-muted">
                    Work Order Type
                  </div>
                  <div className="col-lg-9 col-md-8">
                    {workOrderTypeHelper.getDisplayValue(
                      workOrder.workOrderType,
                    )}
                  </div>
                </div>

                <div className="row">
                  <div className="col-lg-3 col-md-4 text-muted">
                    Service Caller Info
                  </div>
                  <div className="col-lg-9 col-md-8">
                    <div>Name</div>
                    <div className="mt-2">Email</div>
                    <div className="mt-2">Phone</div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-lg-3 col-md-4 text-muted">
                    Location Address
                  </div>
                  <div className="col-lg-9 col-md-8">
                    {workOrder.location.locationName}
                  </div>
                </div>

                <div className="row">
                  <div className="col-lg-3 col-md-4 text-muted">
                    Site Contact Info
                  </div>
                  <div className="col-lg-9 col-md-8">
                    <div>{workOrder.location.siteManagerName}</div>
                    <div className="mt-2">
                      {workOrder.location.siteManagerEmail}
                    </div>
                    <div className="mt-2">
                      {workOrder.location.siteManagerPhone}
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-lg-3 col-md-4 text-muted">
                    Site Instructions
                  </div>
                  <div className="col-lg-9 col-md-8">
                    {workOrder.location.siteInstructions ?? "-"}
                  </div>
                </div>
              </div>

              <div className="col-md-6">
                <h5>Priority / Finance</h5>

                <div className="row">
                  <div className="col-lg-3 col-md-4 text-muted">
                    Scheduled Date
                  </div>
                  <div className="col-lg-9 col-md-8">
                    {workOrder.scheduledDate && (
                      <Moment format={dateTimeHelper.dateFormat}>
                        {workOrder.scheduledDate}
                      </Moment>
                    )}
                    {!workOrder.scheduledDate && <div>-</div>}
                  </div>
                </div>

                <div className="row">
                  <div className="col-lg-3 col-md-4 text-muted">Due Date</div>
                  <div className="col-lg-9 col-md-8">
                    {workOrder.dueDate && (
                      <Moment format={dateTimeHelper.dateFormat}>
                        {workOrder.dueDate}
                      </Moment>
                    )}
                    {!workOrder.dueDate && <div>-</div>}
                  </div>
                </div>

                <div className="row">
                  <div className="col-lg-3 col-md-4 text-muted">Priority</div>
                  <div className="col-lg-9 col-md-8">
                    {workOrderPriorityHelper.getDisplayValue(
                      workOrder.workOrderPriority,
                    )}
                  </div>
                </div>

                <div className="row">
                  <div className="col-lg-3 col-md-4 text-muted">DNE Amount</div>
                  <div className="col-lg-9 col-md-8">{workOrder.dneAmount}</div>
                </div>

                <div className="row">
                  <div className="col-lg-3 col-md-4 text-muted">
                    Scope of Work
                  </div>
                  <div className="col-lg-9 col-md-8">
                    {workOrder.scopeOfWork}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* <!-- Tabs Navigation --> */}
        <div className="card p-2">
          {/* <!-- Tabs Definition --> */}
          <div className="card-header p-0 border-0">
            <ul className="nav nav-tabs profile-tabs" ref={setTabsEl}>
              {hasDispatchesView(workOrder) && (
                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link active"
                    data-bs-toggle="tab"
                    data-bs-target="#dispatches"
                  >
                    Dispatches
                  </button>
                </li>
              )}
              <li className="nav-item" role="presentation">
                <button
                  className={
                    "nav-link" +
                    (!hasDispatchesView(workOrder) ? " active" : "")
                  }
                  data-bs-toggle="tab"
                  data-bs-target="#files"
                >
                  Files
                </button>
              </li>

              {workOrder.parentEntityName && (
                <>
                  <li className="nav-item" role="presentation">
                    <button
                      className={"nav-link"}
                      data-bs-toggle="tab"
                      data-bs-target="#followups"
                    >
                      Followups
                    </button>
                  </li>

                  <li className="nav-item" role="presentation">
                    <button
                      className={"nav-link"}
                      data-bs-toggle="tab"
                      data-bs-target="#proposals"
                    >
                      Proposals for{" "}
                      {ServiceProvider.getNameFromWorkOrderParentEntityName(
                        workOrder,
                      )}{" "}
                      # {workOrder.parentWorkOrderNumber}
                    </button>
                  </li>

                  <li className="nav-item" role="presentation">
                    <button
                      className={"nav-link"}
                      data-bs-toggle="tab"
                      data-bs-target="#invoices"
                    >
                      Invoices for{" "}
                      {ServiceProvider.getNameFromWorkOrderParentEntityName(
                        workOrder,
                      )}{" "}
                      # {workOrder.parentWorkOrderNumber}
                    </button>
                  </li>
                </>
              )}
            </ul>
          </div>

          {/* <!-- Tabs Content --> */}
          <div className="card-body">
            <div className="tab-content">
              {/* <!-- Dispatches Tab --> */}
              {hasDispatchesView(workOrder) && (
                <div className="tab-pane fade active show" id="dispatches">
                  <>
                    {showDispatchesTab && (
                      <WorkOrderDispatchesView workOrder={workOrder} />
                    )}
                  </>
                </div>
              )}

              {/* <!-- Files Tab --> */}
              <div
                className={
                  "tab-pane fade" +
                  (!hasDispatchesView(workOrder) ? " active show" : "")
                }
                id="files"
              >
                {showFilesTab && (
                  <>
                    <WorkOrderFileList
                      idSubscription={subscription.idSubscription}
                      workOrder={workOrder}
                      hasShare={true}
                      hasDelete={true}
                      refreshImageListIndex={refreshImageListIndex}
                    />
                    <WorkOrderFileUpload
                      idSubscription={subscription.idSubscription}
                      workOrderNumber={workOrder.workOrderNumber}
                      onUploadComplete={() =>
                        setRefreshImageListIndex((prev) => prev + 1)
                      }
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      {showAcceptWorkOrderWindow && (
        <AcceptWorkOrderWindow
          setShowAcceptWorkOrderWindow={setShowAcceptWorkOrderWindow}
          onAcceptWorkOrder={onAcceptWorkOrder}
        />
      )}
      {showDeclineWorkOrderWindow && (
        <DeclineWorkOrderWindow
          setShowDeclineWorkOrderWindow={setShowDeclineWorkOrderWindow}
          onDeclineWorkOrder={onDeclineWorkOrder}
        />
      )}
      {showScheduleWorkOrderWindow && (
        <ScheduleWorkOrderWindow
          workOrderScheduledDate={workOrder.scheduledDate}
          setShowScheduleWorkOrderWindow={setShowScheduleWorkOrderWindow}
          onScheduleWorkOrder={onScheduleWorkOrder}
        />
      )}
    </Page>
  );
};

export default WorkOrderView;
